import { useEffect, useRef, useState } from "react";
import AdminLayout from "@/pages/admin/AdminLayout";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import {
  Upload, Loader2, Search, RefreshCw, Palette, Clock, X,
  Download, Check, MessageSquare,
} from "lucide-react";

const CATEGORIES = [
  { v: "label", l: "Label Artwork" },
  { v: "logo", l: "Logo" },
  { v: "packaging", l: "Packaging" },
  { v: "mockup", l: "Mockup" },
  { v: "brand_guidelines", l: "Brand Guidelines" },
  { v: "other", l: "Other" },
];

const STATUS_META = {
  pending: { label: "Awaiting Review", color: "#946600", bg: "rgba(200,162,74,0.14)" },
  approved: { label: "Approved", color: "#1a4a1a", bg: "rgba(74,124,62,0.18)" },
  revision_requested: { label: "Revision Requested", color: "#8a1e1e", bg: "rgba(155,52,52,0.14)" },
};

const AdminArtworkCenter = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [active, setActive] = useState(null);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter) params.set("status", statusFilter);
    api.get(`/admin/portal/artwork?${params.toString()}`)
      .then((r) => setItems(r.data.artworks || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [statusFilter]);

  const filtered = items.filter((a) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (a.client_email || "").toLowerCase().includes(s) ||
      (a.brand_name || "").toLowerCase().includes(s) ||
      (a.title || "").toLowerCase().includes(s)
    );
  });

  return (
    <AdminLayout>
      <div className="mb-6 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="ft-eyebrow mb-2">Client Success</div>
          <h1 className="text-3xl font-bold tracking-tight" data-testid="admin-artwork-title">
            Artwork Approval Center
          </h1>
          <p className="text-[var(--ft-muted)] mt-2 max-w-2xl">
            Upload artwork versions for each client and track approvals in real time.
            Approved files automatically drop into the client&apos;s Brand Vault.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="ft-btn-ghost" data-testid="admin-artwork-refresh">
            <RefreshCw size={14} /> Refresh
          </button>
          <button onClick={() => setShowUpload(true)} className="ft-btn-primary" data-testid="admin-artwork-upload-btn">
            <Upload size={14} /> Upload Version
          </button>
        </div>
      </div>

      <div className="ft-card mb-4">
        <div className="flex gap-3 flex-wrap items-center">
          <div className="flex-1 min-w-[240px] relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ft-muted)]" />
            <input
              className="ft-input pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by client, brand, or title…"
              data-testid="admin-artwork-search"
            />
          </div>
          <div className="flex gap-1 flex-wrap">
            {["", "pending", "approved", "revision_requested"].map((s) => (
              <button
                key={s || "all"}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${statusFilter === s ? "bg-[var(--ft-gold)] text-[var(--ft-black)] border-[var(--ft-gold)]" : "border-[var(--ft-border)] text-[var(--ft-muted)] hover:border-[var(--ft-gold)]"}`}
                data-testid={`admin-artwork-filter-${s || "all"}`}
              >
                {s ? STATUS_META[s]?.label : "All"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="ft-card fx-skeleton h-64" />
      ) : filtered.length === 0 ? (
        <div className="ft-card text-center py-16" data-testid="admin-artwork-empty">
          <Palette size={40} className="mx-auto mb-4 text-[var(--ft-muted)]" />
          <h3 className="text-lg font-semibold">No artwork uploaded yet.</h3>
          <p className="text-[var(--ft-muted)] mt-2 max-w-md mx-auto">
            Upload the first artwork version for a client to kick off the approval flow.
          </p>
        </div>
      ) : (
        <div className="grid gap-3">
          {filtered.map((a) => {
            const meta = STATUS_META[a.status] || STATUS_META.pending;
            return (
              <button
                key={a.id}
                onClick={() => setActive(a)}
                className="ft-card text-left hover:border-[var(--ft-gold)]"
                data-testid={`admin-artwork-row-${a.id}`}
              >
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <div className="ft-eyebrow">{a.category?.replace("_", " ")} · v{a.version}</div>
                    <h3 className="text-lg font-bold tracking-tight mt-1">{a.title}</h3>
                    <div className="text-xs text-[var(--ft-muted)] mt-1">
                      {a.brand_name ? `${a.brand_name} · ` : ""}{a.client_email} ·
                      {" "}Uploaded {new Date(a.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <span
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
                    style={{ background: meta.bg, color: meta.color }}
                  >
                    {meta.label}
                  </span>
                </div>
                {a.review_comment && (
                  <div className="mt-3 pt-3 border-t border-[var(--ft-border)] text-xs">
                    <MessageSquare size={11} className="inline mr-1 text-[var(--ft-gold)]" />
                    <strong>{a.reviewed_by}:</strong> {a.review_comment}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {showUpload && <UploadModal onClose={() => setShowUpload(false)} onDone={() => { setShowUpload(false); load(); }} />}
      {active && <AdminArtDrawer art={active} onClose={() => setActive(null)} />}
    </AdminLayout>
  );
};

const UploadModal = ({ onClose, onDone }) => {
  const [form, setForm] = useState({
    client_email: "",
    brand_name: "",
    brand_slug: "",
    category: "label",
    title: "",
    notes: "",
  });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const submit = async () => {
    if (!form.client_email.trim()) return toast.error("Client email is required");
    if (!file) return toast.error("Please choose a file");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const params = new URLSearchParams();
      params.set("client_email", form.client_email.trim().toLowerCase());
      if (form.brand_name) params.set("brand_name", form.brand_name);
      if (form.brand_slug) params.set("brand_slug", form.brand_slug);
      params.set("category", form.category);
      if (form.title) params.set("title", form.title);
      if (form.notes) params.set("notes", form.notes);
      await api.post(`/admin/portal/artwork/upload?${params.toString()}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Artwork uploaded and client notified.");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="admin-artwork-upload-modal">
      <button className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} aria-label="Close" />
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl">
        <div className="px-6 py-4 border-b border-[var(--ft-border)] flex items-center justify-between">
          <h3 className="text-lg font-bold tracking-tight">Upload Artwork Version</h3>
          <button onClick={onClose} className="ft-btn-ghost"><X size={16} /></button>
        </div>
        <div className="p-6 space-y-4">
          <Field label="Client Email *">
            <input className="ft-input" value={form.client_email} onChange={(e) => setForm((f) => ({ ...f, client_email: e.target.value }))} data-testid="admin-artwork-upload-email" />
          </Field>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Brand Name">
              <input className="ft-input" value={form.brand_name} onChange={(e) => setForm((f) => ({ ...f, brand_name: e.target.value }))} data-testid="admin-artwork-upload-brand" />
            </Field>
            <Field label="Brand Slug">
              <input className="ft-input" value={form.brand_slug} onChange={(e) => setForm((f) => ({ ...f, brand_slug: e.target.value }))} />
            </Field>
          </div>
          <Field label="Category">
            <select className="ft-input" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} data-testid="admin-artwork-upload-category">
              {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
            </select>
          </Field>
          <Field label="Title">
            <input className="ft-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Auto-filled from filename if empty" />
          </Field>
          <Field label="Notes for client">
            <textarea className="ft-input min-h-[70px]" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
          </Field>
          <Field label="File *">
            <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="admin-artwork-upload-file" />
          </Field>
        </div>
        <div className="px-6 py-4 border-t border-[var(--ft-border)] flex justify-end gap-2">
          <button onClick={onClose} className="ft-btn-ghost">Cancel</button>
          <button onClick={submit} disabled={uploading} className="ft-btn-primary" data-testid="admin-artwork-upload-submit">
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminArtDrawer = ({ art, onClose }) => {
  const token = typeof window !== "undefined" ? localStorage.getItem("flowtalis_token") : null;
  const previewSrc = `${API}/portal/artwork/${art.id}/download?auth=${encodeURIComponent(token || "")}`;
  const isImage = (art.content_type || "").startsWith("image/");
  const isPdf = (art.content_type || "") === "application/pdf";
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="admin-artwork-drawer">
      <button className="flex-1 bg-black/50 backdrop-blur-sm" onClick={onClose} aria-label="Close" />
      <div className="w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-[var(--ft-border)] px-6 py-4 flex items-center justify-between">
          <div>
            <div className="ft-eyebrow">{art.category?.replace("_", " ")} · v{art.version}</div>
            <h3 className="text-lg font-bold tracking-tight">{art.title}</h3>
          </div>
          <button onClick={onClose} className="ft-btn-ghost"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="text-sm">
            <div><strong>Client:</strong> {art.client_email}</div>
            {art.brand_name && <div><strong>Brand:</strong> {art.brand_name}</div>}
            <div><strong>Uploaded by:</strong> {art.uploaded_by}</div>
            {art.reviewed_by && (
              <div><strong>Reviewed by:</strong> {art.reviewed_by} · <em>{art.review_comment}</em></div>
            )}
          </div>
          <div className="rounded-lg bg-[var(--ft-soft-gray)] border border-[var(--ft-border)] overflow-hidden">
            {isImage ? <img src={previewSrc} className="w-full max-h-[560px] object-contain bg-white" alt="" />
             : isPdf ? <iframe title={art.title} src={previewSrc} className="w-full h-[560px] bg-white" />
             : <div className="p-8 text-center text-sm text-[var(--ft-muted)]">No inline preview. Download to view.</div>}
          </div>
          <a href={previewSrc} download={art.original_filename} className="ft-btn-secondary inline-flex" data-testid="admin-artwork-download">
            <Download size={14} /> Download original
          </a>
          <div>
            <div className="ft-eyebrow mb-2">Activity Timeline</div>
            <ul className="space-y-2">
              {[...(art.activity || [])].reverse().map((a, i) => (
                <li key={i} className="flex gap-2 text-xs">
                  <Clock size={10} className="mt-1 text-[var(--ft-gold)] shrink-0" />
                  <span className="text-[var(--ft-muted)]">{new Date(a.at).toLocaleString()}</span>
                  <span><strong>{a.actor}</strong> · {a.detail}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="ft-label block mb-1.5">{label}</label>
    {children}
  </div>
);

export default AdminArtworkCenter;
